/**
 * Reduce en el navegador una foto que pesa más de lo permitido.
 *
 * Las cámaras de los celulares nuevos sacan fotos de 10 MB o más y el
 * servidor acepta hasta 8 MB: en vez de pedirle al papá que «suba una más
 * ligera» (no sabe cómo), se achica aquí y se manda como JPG. Solo se usa
 * cuando el archivo original NO cabe; una foto que cabe viaja tal cual.
 * Devuelve `null` si el navegador no puede (entonces se muestra el aviso de
 * tamaño de siempre).
 */

const ATTEMPTS: Array<{ maxSide: number; quality: number }> = [
  { maxSide: 2000, quality: 0.85 },
  { maxSide: 1400, quality: 0.8 },
  { maxSide: 1000, quality: 0.75 },
];

interface LoadedImage {
  source: CanvasImageSource;
  width: number;
  height: number;
  release: () => void;
}

async function loadImage(file: File): Promise<LoadedImage | null> {
  if (typeof createImageBitmap === "function") {
    try {
      // "from-image": respeta la orientación con la que se tomó la foto.
      const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
      return { source: bitmap, width: bitmap.width, height: bitmap.height, release: () => bitmap.close() };
    } catch {
      // Navegador sin esa opción o imagen que no pudo leer: se intenta abajo.
    }
  }
  if (typeof URL === "undefined" || typeof URL.createObjectURL !== "function") return null;
  const url = URL.createObjectURL(file);
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(new Error("image_load_failed"));
      element.src = url;
    });
    return {
      source: image,
      width: image.naturalWidth,
      height: image.naturalHeight,
      release: () => URL.revokeObjectURL(url),
    };
  } catch {
    URL.revokeObjectURL(url);
    return null;
  }
}

export async function shrinkImageToFit(file: File, maxBytes: number): Promise<File | null> {
  if (typeof document === "undefined") return null;
  let loaded: LoadedImage | null = null;
  try {
    loaded = await loadImage(file);
    if (!loaded || loaded.width <= 0 || loaded.height <= 0) return null;
    for (const attempt of ATTEMPTS) {
      const scale = Math.min(1, attempt.maxSide / Math.max(loaded.width, loaded.height));
      const width = Math.max(1, Math.round(loaded.width * scale));
      const height = Math.max(1, Math.round(loaded.height * scale));
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext("2d");
      if (!context) return null;
      // Fondo blanco: un PNG con transparencia no debe quedar negro en JPG.
      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, width, height);
      context.drawImage(loaded.source, 0, 0, width, height);
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, "image/jpeg", attempt.quality),
      );
      if (blob && blob.size > 0 && blob.size <= maxBytes) {
        return new File([blob], "foto.jpg", { type: "image/jpeg" });
      }
    }
    return null;
  } catch {
    return null;
  } finally {
    loaded?.release();
  }
}
